import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useGetRestaurantUsersApi } from './use-get-restaurant-users-api';

const endpoint = 'ums/v1/restaurants/users';
export const toggleRestaurantUserActivationEndpointIdentifier = endpoint;

interface ToggleActivationBodyType {
  isActive: boolean;
}

type TData = Record<string, any>;
type TError = AxiosError;

type TVariables = {
  body: ToggleActivationBodyType;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useToggleRestaurantUserActivationApi(
  restaurantId: string,
  userId: number,
  options?: Options
): ReturnType {
  const toast = useCustomToast();
  const fullEndpoint = `${endpoint}/${restaurantId}/${userId}/activation`;

  const { refetch: refetchRestaurantUsersApi } = useGetRestaurantUsersApi(restaurantId, { enabled: false });

  const mutationFn = async ({ body }: TVariables) => {
    try {
      const { data } = await http.patch(fullEndpoint, body);

      // Refetch restaurant users
      refetchRestaurantUsersApi();

      toast({
        description: `User has been ${body.isActive ? 'activated' : 'deactivated'} successfully`,
        status: 'success',
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, { mutationKey: fullEndpoint, ...options });
}
